import React, { useState } from 'react'
import { Button } from '../../ui/Button'
import { Input } from '../../ui/Input'
import type { StepData } from '../../../services/onboardingService'

interface PasswordChangeStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
}

const PasswordChangeStep: React.FC<PasswordChangeStepProps> = ({
  onComplete,
  stepData,
}) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida'
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida'
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu nueva contraseña'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return
    if (!onComplete) return

    setLoading(true)

    try {
      // Aquí iría la lógica para cambiar la contraseña
      // await changePassword(formData.currentPassword, formData.newPassword);

      // Simular llamada a API
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Marcar paso como completado
      await onComplete({
        passwordChanged: true,
        newPasswordStrength: getPasswordStrength(formData.newPassword),
        completedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error cambiando contraseña:', error)
      setErrors({
        submit: 'Error al cambiar la contraseña. Intenta nuevamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const getPasswordStrength = (password: string): string => {
    const hasLower = /[a-z]/.test(password)
    const hasUpper = /[A-Z]/.test(password)
    const hasNumber = /\d/.test(password)
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    const isLongEnough = password.length >= 8

    const criteria = [hasLower, hasUpper, hasNumber, hasSpecial, isLongEnough]
    const metCriteria = criteria.filter(Boolean).length

    if (metCriteria === 5) return 'strong'
    if (metCriteria >= 3) return 'medium'
    return 'weak'
  }

  const getPasswordStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong':
        return 'text-green-600'
      case 'medium':
        return 'text-yellow-600'
      case 'weak':
        return 'text-red-600'
      default:
        return 'text-gray-400'
    }
  }

  const passwordStrength = formData.newPassword
    ? getPasswordStrength(formData.newPassword)
    : ''

  return (
    <div className="max-w-md mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Cambio de contraseña
        </h2>
        <p className="text-gray-600">
          Usuario debe cambiar su contraseña inicial
        </p>
      </div>

      {stepData?.status === 'completed' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-green-600 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-green-800 font-medium">
              Contraseña actualizada exitosamente
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label="Contraseña Actual"
            type="password"
            value={formData.currentPassword}
            onChange={e =>
              setFormData({ ...formData, currentPassword: e.target.value })
            }
            error={errors.currentPassword}
            required
          />
        </div>

        <div>
          <Input
            label="Nueva Contraseña"
            type="password"
            value={formData.newPassword}
            onChange={e =>
              setFormData({ ...formData, newPassword: e.target.value })
            }
            error={errors.newPassword}
            required
          />

          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Seguridad:</span>
                <span
                  className={`text-sm font-medium ${getPasswordStrengthColor(passwordStrength)}`}
                >
                  {passwordStrength === 'strong'
                    ? 'Fuerte'
                    : passwordStrength === 'medium'
                      ? 'Media'
                      : 'Débil'}
                </span>
              </div>

              <div className="mt-1 text-xs text-gray-500">
                <p>La contraseña debe contener:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li
                    className={
                      formData.newPassword.length >= 8 ? 'text-green-600' : ''
                    }
                  >
                    Al menos 8 caracteres
                  </li>
                  <li
                    className={
                      /[a-z]/.test(formData.newPassword) ? 'text-green-600' : ''
                    }
                  >
                    Una letra minúscula
                  </li>
                  <li
                    className={
                      /[A-Z]/.test(formData.newPassword) ? 'text-green-600' : ''
                    }
                  >
                    Una letra mayúscula
                  </li>
                  <li
                    className={
                      /\d/.test(formData.newPassword) ? 'text-green-600' : ''
                    }
                  >
                    Un número
                  </li>
                  <li
                    className={
                      /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)
                        ? 'text-green-600'
                        : ''
                    }
                  >
                    Un carácter especial
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div>
          <Input
            label="Confirmar Nueva Contraseña"
            type="password"
            value={formData.confirmPassword}
            onChange={e =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            error={errors.confirmPassword}
            required
          />
        </div>

        {errors.submit && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{errors.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={loading || stepData?.status === 'completed'}
          className="w-full"
        >
          {loading
            ? 'Cambiando Contraseña...'
            : stepData?.status === 'completed'
              ? 'Contraseña Actualizada'
              : 'Cambiar Contraseña'}
        </Button>
      </form>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800 mb-1">
              ¿Por qué cambiar tu contraseña?
            </h4>
            <p className="text-sm text-blue-700">
              Por tu seguridad, requerimos que actualices tu contraseña para
              acceder a las funciones completas de la plataforma de leasing.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordChangeStep
